import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "./api.service";
import { AuthenticationService } from "./authentication.service";
import { MessagesService } from "./messages.service";
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, of, ReplaySubject, tap, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { environment } from "environments/environment";


@Injectable({
    providedIn: 'root'
})
export class NavigationCustomService implements OnDestroy {
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    private unreadMessagesCount: number = 0;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(private http: HttpClient, router: Router, public authSrv: AuthenticationService, private messagesService: MessagesService) {
        // Subscribe to unread messages count with distinctUntilChanged to avoid unnecessary updates
        // Se desuscribe cuando el usuario cierra sesión
        this.messagesService.unreadCount$
            .pipe(
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(count => {
                // Only update if count actually changed
                if (this.unreadMessagesCount !== count) {
                    this.unreadMessagesCount = count;
                    this.refreshNavigation();
                }
            });

        // Detener suscripciones cuando usuario cierra sesión
        this.authSrv.currentUser
            ?.pipe(takeUntil(this._unsubscribeAll))
            .subscribe(user => {
                if (!user) {
                    this._unsubscribeAll.next(null);
                    return;
                }

                // Refrescar navegación si cambia usuario/empresa activa
                this.refreshNavigation();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    get(): Observable<Navigation>
    {
        let nav = this.getNavigation();
        let navFull: Navigation = { 'default': nav, 'compact': nav, 'futuristic': nav, 'horizontal' : nav };
        this._navigation.next(navFull);

        return of(navFull);
    }

    refreshNavigation(): void {
        let nav = this.getNavigation();
        let navFull: Navigation = { 'default': nav, 'compact': nav, 'futuristic': nav, 'horizontal' : nav };
        this._navigation.next(navFull);
    }

    getNavigation(): FuseNavigationItem[]{

        let navigation: FuseNavigationItem[]|any[];

        // Crear mensaje item con badge condicional
        const getMessagesItem = () => {
            let messagesItem: any = {
                id   : 'messages',
                title: 'Menu.Messages',
                type : 'basic',
                icon : 'heroicons_outline:mail',
                link : '/messages'
            };

            if(this.unreadMessagesCount > 0){
                messagesItem.badge = {
                    title  : this.unreadMessagesCount.toString(),
                    classes: 'w-5 h-5 bg-white text-hr rounded-full'
                };
            }

            return messagesItem;
        };

        const showBolsaStats = (() => {
            const user = this.authSrv.currentUserValue;
            if (!user) {
                return false;
            }

            // Reutilizamos el flag de backend: solo quien puede gestionar disability
            // debe ver el apartado de Estadísticas Bolsa.
            return (user as any)?.can_manage_disability === true;
        })();

        const getBolsaStatsItem = () => {
            return {
                id   : 'estadisticas-bolsa',
                title: 'Menu.IlersisBolsaStatistics',
                type : 'basic',
                icon : 'heroicons_outline:chart-bar',
                link : '/estadisticas-bolsa'
            };
        };

        if(this.authSrv.currentUserValue && this.authSrv.currentUserValue.isAdmin){

            navigation = [
                {
                    id   : 'home',
                    title: 'Menu.Home',
                    type : 'basic',
                    icon : 'heroicons_outline:home',
                    link : '/dashboard'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },

                {
                    id   : 'users',
                    title: 'Menu.Users',
                    type : 'basic',
                    icon : 'heroicons_outline:user-group',
                    link : '/users'
                },
                getMessagesItem(),
                {
                    id   : 'companies',
                    title: 'Menu.Companies',
                    type : 'basic',
                    icon : 'mat_outline:business',
                    link : '/companies'
                },
                {
                    id   : 'professional-profile',
                    title: 'Menu.ProfessionalProfile',
                    type : 'basic',
                    icon : 'heroicons_outline:user-add',
                    link : '/professional-profiles'
                },
                {
                    id   : 'professional-profile-group',
                    title: 'Menu.ProfessionalProfileGroup',
                    type : 'basic',
                    icon : 'heroicons_outline:user-group',
                    link : '/professional-profiles/groups'
                },
                {
                    id   : 'competences',
                    title: 'Menu.Competences',
                    type : 'basic',
                    icon : 'mat_outline:auto_stories',
                    link : '/competences'
                },
                {
                    id: 'courses',
                    title: 'Menu.Training',
                    type: 'basic',
                    icon: 'mat_outline:library_books',
                    link: '/courses'
                },
                ...(showBolsaStats ? [getBolsaStatsItem()] : []),
                (environment.showDataLoader)?{
                    id   : 'apps.data-loader',
                    title: 'Menu.DataLoader',
                    type : 'basic',
                    icon: 'heroicons_outline:cloud-upload',
                    link : '/data-loader'
                }:{},
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'competences',
                    title: 'Menu.Assessments',
                    type : 'basic',
                    icon : 'mat_outline:assignment',
                    link : '/assessments'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'account',
                    title: 'Menu.Account',
                    type : 'basic',
                    icon : 'mat_outline:settings',
                    link : '/account'
                },
                (environment.showFileManager)?{
                    id   : 'apps.file-manager',
                    title: 'Menu.Resources',
                    type : 'basic',
                    icon: 'heroicons_outline:folder',
                    link : '/file-manager'
                }:{},
                {
                    id      : 'apps.help-center',
                    title   : 'Help Center',
                    type    : 'collapsable',
                    icon    : 'heroicons_outline:support',
                    link    : '/helper-center',
                    children: [
                        (environment.showFAQs)?{
                            id   : 'apps.help-center.faqs',
                            title: 'FAQs',
                            type : 'basic',
                            link : '/faqs'
                        }:{},
                        {
                            id   : 'apps.help-center.support',
                            title: 'Soporte',
                            type : 'basic',
                            link : '/helper-center/support'
                        }
                    ]
                },
            ];
        }else if(this.authSrv.currentUserValue && (this.authSrv.currentUserValue.isBusinessAdmin || this.authSrv.currentUserValue.isBusinessController)){
            navigation = [
                {
                    id   : 'home',
                    title: 'Menu.Home',
                    type : 'basic',
                    icon : 'heroicons_outline:home',
                    link : '/dashboard'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },

                {
                    id   : 'users',
                    title: 'Menu.Employees',
                    type : 'basic',
                    icon : 'heroicons_outline:user-group',
                    link : '/users'
                },
                getMessagesItem(),
                ...(showBolsaStats ? [getBolsaStatsItem()] : []),
                {
                    id   : 'company',
                    title: 'Menu.Company',
                    type : 'basic',
                    icon : 'mat_outline:business',
                    link : '/company'
                },
                {
                    id   : 'professional-profile',
                    title: 'Menu.ProfessionalProfile',
                    type : 'basic',
                    icon : 'heroicons_outline:user-add',
                    link : '/professional-profiles'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'competences',
                    title: 'Menu.Assessments',
                    type : 'basic',
                    icon : 'mat_outline:assignment',
                    link : '/assessments'
                },
                {
                    id: 'courses',
                    title: 'Menu.Training',
                    type: 'basic',
                    icon: 'mat_outline:library_books',
                    link: '/courses'
                },
                {
                    id   : 'objectives',
                    title: 'Menu.Objectives',
                    type : 'basic',
                    icon : 'heroicons_outline:academic-cap',
                    link : '/objectives'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'account',
                    title: 'Menu.Account',
                    type : 'basic',
                    icon : 'mat_outline:settings',
                    link : '/account'
                },
                (environment.showFileManager)?{
                    id   : 'apps.file-manager',
                    title: 'Menu.Resources',
                    type : 'basic',
                    icon: 'heroicons_outline:folder',
                    link : '/file-manager'
                }:{},
                {
                    id      : 'apps.help-center',
                    title   : 'Help Center',
                    type    : 'collapsable',
                    icon    : 'heroicons_outline:support',
                    link    : '/helper-center',
                    children: [
                        (environment.showFAQs)?{
                            id   : 'apps.help-center.faqs',
                            title: 'FAQs',
                            type : 'basic',
                            link : '/faqs'
                        }:{},
                        {
                            id   : 'apps.help-center.support',
                            title: 'Soporte',
                            type : 'basic',
                            link : '/helper-center/support'
                        }
                    ]
                },
            ];
        }else if(this.authSrv.currentUserValue && (this.authSrv.currentUserValue.isProfessionalFreelance )){
            navigation = [
                {
                    id   : 'home',
                    title: 'Menu.Home',
                    type : 'basic',
                    icon : 'heroicons_outline:home',
                    link : '/dashboard'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },

                {
                    id   : 'users',
                    title: 'Menu.Employees',
                    type : 'basic',
                    icon : 'heroicons_outline:user-group',
                    link : '/users'
                },
                getMessagesItem(),
                ...(showBolsaStats ? [getBolsaStatsItem()] : []),
                {
                    id   : 'companies',
                    title: 'Menu.Companies',
                    type : 'basic',
                    icon : 'mat_outline:business',
                    link : '/companies'
                },
                {
                    id   : 'professional-profile',
                    title: 'Menu.ProfessionalProfile',
                    type : 'basic',
                    icon : 'heroicons_outline:user-add',
                    link : '/professional-profiles'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'competences',
                    title: 'Menu.Assessments',
                    type : 'basic',
                    icon : 'mat_outline:assignment',
                    link : '/assessments'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'account',
                    title: 'Menu.Account',
                    type : 'basic',
                    icon : 'mat_outline:settings',
                    link : '/account'
                },
                {
                    id      : 'apps.help-center',
                    title   : 'Help Center',
                    type    : 'collapsable',
                    icon    : 'heroicons_outline:support',
                    link    : '/helper-center',
                    children: [
                        (environment.showFAQs)?{
                            id   : 'apps.help-center.faqs',
                            title: 'FAQs',
                            type : 'basic',
                            link : '/faqs'
                        }:{},
                        {
                            id   : 'apps.help-center.support',
                            title: 'Soporte',
                            type : 'basic',
                            link : '/helper-center/support'
                        }
                    ]
                },
            ];

        }else if( this.authSrv.currentUserValue && (this.authSrv.currentUserValue.isConsultor || this.authSrv.currentUserValue.isConsultorPice)){
            console.log(this.authSrv.currentUserValue?.isConsultorPice, this.authSrv.currentUserValue?.isConsultor);
            navigation = [
                {
                    id   : 'home',
                    title: 'Menu.Home',
                    type : 'basic',
                    icon : 'heroicons_outline:home',
                    link : '/dashboard'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'users',
                    title: 'Menu.Employees',
                    type : 'basic',
                    icon : 'heroicons_outline:user-group',
                    link : '/users'
                },
                getMessagesItem(),
                {
                    id   : 'companies',
                    title: 'Menu.Companies',
                    type : 'basic',
                    icon : 'mat_outline:business',
                    link : '/companies'
                },
                {
                    id   : 'professional-profile',
                    title: 'Menu.ProfessionalProfile',
                    type : 'basic',
                    icon : 'heroicons_outline:user-add',
                    link : '/professional-profiles'
                },
                {
                    id  : 'divider-2',
                    type: 'divider'
                },
                {
                    id   : 'competences',
                    title: 'Menu.Assessments',
                    type : 'basic',
                    icon : 'mat_outline:assignment',
                    link : '/assessments'
                },
                {
                    id  : 'divider-3',
                    type: 'divider'
                },
                {
                    id   : 'account',
                    title: 'Menu.Account',
                    type : 'basic',
                    icon : 'mat_outline:settings',
                    link : '/account'
                },
                (environment.showFileManager)?{
                    id   : 'apps.file-manager',
                    title: 'Menu.Resources',
                    type : 'basic',
                    icon : 'heroicons_outline:folder',
                    link : '/file-manager'
                }:{},
                {
                    id      : 'apps.help-center',
                    title   : 'Help Center',
                    type    : 'collapsable',
                    icon    : 'heroicons_outline:support',
                    link    : '/helper-center',
                    children: [
                        (environment.showFAQs)?{
                            id   : 'apps.help-center.faqs',
                            title: 'FAQs',
                            type : 'basic',
                            link : '/faqs'
                        }:{},
                        {
                            id   : 'apps.help-center.support',
                            title: 'Soporte',
                            type : 'basic',
                            link : '/helper-center/support'
                        }
                    ]
                },
            ];

        }else if(this.authSrv.currentUserValue){
            navigation = [
                {
                    id   : 'home',
                    title: 'Menu.Home',
                    type : 'basic',
                    icon : 'heroicons_outline:home',
                    link : '/dashboard'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },

                getMessagesItem(),

                ...(showBolsaStats ? [getBolsaStatsItem()] : []),

                {
                    id   : 'competences',
                    title: 'Menu.Assessments',
                    type : 'basic',
                    icon : 'mat_outline:assignment',
                    link : '/assessments'
                },
                {
                    id  : 'divider-1',
                    type: 'divider'
                },
                {
                    id   : 'account',
                    title: 'Menu.Account',
                    type : 'basic',
                    icon : 'mat_outline:settings',
                    link : '/account'
                },
                (environment.showFileManager)?{
                    id   : 'apps.file-manager',
                    title: 'Menu.Resources',
                    type : 'basic',
                    icon: 'heroicons_outline:folder',
                    link : '/file-manager'
                }:{},
                {
                    id      : 'apps.help-center',
                    title   : 'Help Center',
                    type    : 'collapsable',
                    icon    : 'heroicons_outline:support',
                    link    : '/helper-center',
                    children: [
                        (environment.showFAQs)?{
                            id   : 'apps.help-center.faqs',
                            title: 'FAQs',
                            type : 'basic',
                            link : '/faqs'
                        }:{},
                        {
                            id   : 'apps.help-center.support',
                            title: 'Soporte',
                            type : 'basic',
                            link : '/helper-center/support'
                        }
                    ]
                },
            ];
        }else{
            navigation = [];
        }

        if (this.authSrv.currentUserValue && this.authSrv.currentUserValue.isConsultorPice) {
            navigation = navigation.filter(n => n.id !== 'professional-profile');
        }

        return this.groupNavigation(navigation);

    }

    private groupNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
        const groups: { id: string; title: string; items: FuseNavigationItem[] }[] = [
            { id: 'general', title: 'General', items: [] },
            { id: 'ecosystem', title: 'Ecosistema', items: [] },
            { id: 'model', title: 'Modelo', items: [] },
            { id: 'support', title: 'Soporte', items: [] }
        ];

        const getGroup = (item: FuseNavigationItem): typeof groups[number] => {
            if (item.link === '/dashboard' || item.link === '/messages' || item.link === '/assessments') {
                return groups[0];
            }

            if (
                item.id === 'users' ||
                item.id === 'companies' ||
                item.id === 'company' ||
                item.id === 'professional-profile' ||
                item.id === 'professional-profile-group'
            ) {
                return groups[1];
            }

            if (
                item.id === 'competences' ||
                item.id === 'courses' ||
                item.id === 'objectives' ||
                item.id === 'estadisticas-bolsa' ||
                item.id === 'apps.data-loader'
            ) {
                return groups[2];
            }

            return groups[3];
        };

        for (const item of items) {
            if (!item || !item.type || item.type === 'divider') {
                continue;
            }

            const group = getGroup(item);
            group.items.push(item);
        }

        const groupedNavigation: FuseNavigationItem[] = groups
            .slice(1)
            .filter(group => group.items.length > 0)
            .map(group => ({
                id: `navigation-${group.id}`,
                title: group.title,
                type: 'group' as const,
                children: group.items
            }));

        return [
            ...groups[0].items,
            ...groupedNavigation
        ];
    }



}
